// `templates`: Swift `MessageTemplate` (GroundTeam/Models/Automation.swift): reusable email / text bodies with merge
// fields. Decoding template `MessageTemplate(name: "")` (GroundTeam/Sync/ResilientDecoding.swift:121).

import { MESSAGE_CHANNELS, type MessageChannel } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  DATE,
  STRING,
  UUID,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface TemplatePayload extends Passthrough {
  id: string;
  name: string;
  channel: MessageChannel;
  subject: string;
  body: string;
  createdAt: string;
}

export const TEMPLATE_KEYS = [
  'id',
  'name',
  'channel',
  'subject',
  'body',
  'createdAt',
] as const satisfies readonly (keyof Known<TemplatePayload>)[];

/** `MessageTemplate.mergeFields`: the placeholders the phone substitutes when sending. */
export const TEMPLATE_MERGE_FIELDS = [
  '{{first_name}}',
  '{{name}}',
  '{{company}}',
  '{{address}}',
  '{{rep_name}}',
  '{{rep_phone}}',
  '{{company_name}}',
] as const;

export const TEMPLATE_FIELDS: FieldMap<TemplatePayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  channel: field.req(enumOf('MessageChannel', MESSAGE_CHANNELS), () => 'email'),
  subject: field.req(STRING, () => ''),
  body: field.req(STRING, () => ''),
  createdAt: field.req(DATE, nowWire),
};

export function decodeTemplate(raw: JsonObject, envelopeId?: string): Decoded<TemplatePayload> {
  return decodeModel(TEMPLATE_FIELDS, raw, {}, envelopeId);
}

export type TemplateInit = Partial<Known<TemplatePayload>> & { name: string };

export function newTemplate(init: TemplateInit): TemplatePayload {
  return createModel(
    TEMPLATE_FIELDS,
    { id: newId(), channel: 'email', subject: '', body: '', createdAt: nowWire() },
    init,
    'templates',
  );
}
